import { Box, CircularProgress, Typography } from '@mui/material'
import { useAuth0 } from '@auth0/auth0-react'
import React, { useEffect, useRef } from 'react'
import { Helmet } from 'react-helmet'
import { useParams } from 'react-router-dom'
import EmptyScreen from '../../../components/EmptyScreen'
import { getLocation } from '../../../helpers/location-helper'
import { ROUTES } from '../../../helpers/routes-helper'
import useAppUtilities from '../../../hooks/useAppUtilities'
import { useFoodAreaRestaurantsPoll } from '../../../hooks/useFoodAreaRestaurantsPoll'
import { useAppSelector } from '../../../store'
import { selectLocation } from '../../../store/authSlice'
import {
  enrichFoodAreaRestaurantsWithPredictions,
  getFoodAreaRestaurants,
  selectFoodAreaRestaurants,
  selectFoodAreasLoading,
} from '../../../store/foodAreaSlice'
import { MuiBackdrop } from '../styled/global.styled'
import RestaurantList from './components/RestaurantList'
import {
  RestaurantsContainer,
  RestaurantsContentContainer,
  RestaurantsHeaderBackRoundedButton,
  RestaurantsHeaderContainer,
  RestaurantsHeaderInnerContainer,
  RestaurantsHeaderTitleContainer,
  RestaurantsHeaderTitleTypography,
  RestaurantsIconChevronLeft,
  RestaurantsMainContainer,
} from './styled/Restaurants.styled'

const FoodAreaRestaurantsScreen: React.FC = () => {
  const { navigate, location, dispatch } = useAppUtilities()
  const { isAuthenticated } = useAuth0()
  const { foodAreaId } = useParams<{ foodAreaId: string }>()
  const areaName: string =
    (location.state as { areaName?: string } | null)?.areaName ?? 'Neighborhood'
  const restaurants = useAppSelector(selectFoodAreaRestaurants)
  const isLoading = useAppSelector(selectFoodAreasLoading)
  const savedLocation = useAppSelector(selectLocation)
  const hasEnrichedRef = useRef(false)

  // When a freshly-seeded area returns zero restaurants, poll a few times
  // (backend may still be ingesting) before showing the empty state.
  const isBackfilling = useFoodAreaRestaurantsPoll(
    {
      foodAreaId,
      latitude: savedLocation?.latitude,
      longitude: savedLocation?.longitude,
    },
    restaurants.length,
    isLoading,
  )

  useEffect(() => {
    if (foodAreaId) {
      hasEnrichedRef.current = false
      void dispatch(getFoodAreaRestaurants({
        foodAreaId,
        latitude: savedLocation?.latitude,
        longitude: savedLocation?.longitude,
      }))
    }
  }, [foodAreaId, savedLocation, dispatch])

  // Enrichment effect: fetch predictions after base list loads (only once per area)
  useEffect(() => {
    const enrich = async (): Promise<void> => {
      if (
        !isAuthenticated ||
        isLoading ||
        restaurants.length === 0 ||
        hasEnrichedRef.current
      ) {
        return
      }

      hasEnrichedRef.current = true
      const local = savedLocation ?? (await getLocation())
      const aliases = restaurants
        .map((r) => r.restaurantDTO?.alias)
        .filter((a): a is string => !!a)

      if (aliases.length === 0) return

      void dispatch(
        enrichFoodAreaRestaurantsWithPredictions({
          aliases,
          includeMenus: false,
          longitude: local?.longitude,
          latitude: local?.latitude,
        }),
      )
    }

    void enrich()
  }, [isAuthenticated, isLoading, restaurants, savedLocation, dispatch])

  return (
    <RestaurantsMainContainer>
      <Helmet>
        <title>{`Resactly | ${areaName}`}</title>
        <meta
          name="description"
          content={`Browse restaurants in ${areaName} on Resactly.`}
        />
      </Helmet>

      {(!isLoading || isBackfilling) && (
        <RestaurantsContainer>
          <RestaurantsHeaderContainer>
            <RestaurantsHeaderInnerContainer>
              <RestaurantsHeaderTitleContainer>
                <RestaurantsHeaderBackRoundedButton
                  onClick={() => navigate(ROUTES.HOME)}
                >
                  <RestaurantsIconChevronLeft />
                </RestaurantsHeaderBackRoundedButton>
                <RestaurantsHeaderTitleTypography>
                  {areaName}
                </RestaurantsHeaderTitleTypography>
              </RestaurantsHeaderTitleContainer>
            </RestaurantsHeaderInnerContainer>
          </RestaurantsHeaderContainer>

          <RestaurantsContentContainer>
            {restaurants.length > 0 ? (
              <RestaurantList
                loading={false}
                restaurants={restaurants}
                screenName="food_area"
              />
            ) : isBackfilling ? (
              <Box
                height="400px"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                gap="16px"
              >
                <CircularProgress />
                <Typography>Finding restaurants nearby…</Typography>
              </Box>
            ) : (
              <EmptyScreen
                mainText="No restaurants yet"
                secondaryText="Check back soon for restaurants in this area"
                imageUrl="/images/empty-screen.svg"
                containerHeight="400px"
              />
            )}
          </RestaurantsContentContainer>
        </RestaurantsContainer>
      )}

      <MuiBackdrop open={isLoading && !isBackfilling}>
        <CircularProgress color="inherit" />
      </MuiBackdrop>
    </RestaurantsMainContainer>
  )
}

export default FoodAreaRestaurantsScreen
